import React, { memo, useCallback } from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { useForm } from "react-hook-form";
import { FormControl, FormLabel, Input, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Stack, Text } from "@chakra-ui/react";
import { NodeType } from "../../../models/nodeType";
import { FlowNodeWithChildren } from "./FlowNode";

interface FormData {
  npcIdsText: string;
  radius: number;
  minPeriodSeconds: number;
  maxPeriodSeconds: number;
  minDistFromCenter: number;
}

type NodeDataShape = {
  npcIds?: string[];
  radius?: number;
  minPeriodSeconds?: number;
  maxPeriodSeconds?: number;
  minDistFromCenter?: number;
};

function parseNpcIds(text: string): string[] {
  return text
    .split(/[,\n\t ]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const NpcWanderingModeStartNode: React.FC<NodeProps<NodeType>> = (props) => {
  const {
    id: nodeId,
    data: { color, nodeData = [] },
  } = props;

  const formData = nodeData as NodeDataShape | undefined;

  const npcIds = formData?.npcIds ?? [];
  const radius = formData?.radius ?? 128;
  const minPeriodSeconds = formData?.minPeriodSeconds ?? 3;
  const maxPeriodSeconds = formData?.maxPeriodSeconds ?? 5;
  const minDistFromCenter = formData?.minDistFromCenter ?? 0;

  const { register, handleSubmit } = useForm<FormData>({
    defaultValues: {
      npcIdsText: npcIds.join(","),
      radius,
      minPeriodSeconds,
      maxPeriodSeconds,
      minDistFromCenter,
    },
    mode: "onChange",
  });

  const reactFlow = useReactFlow();

  const handleUpdate = useCallback(
    ({ npcIdsText, radius, minPeriodSeconds, maxPeriodSeconds, minDistFromCenter }: FormData) => {
      reactFlow.setNodes((nodes) => {
        const node = nodes.find(({ id }) => id === nodeId);
        if (node == null) return nodes;

        const nodeDataCloned = structuredClone(node.data) as NodeType;
        const next = (nodeDataCloned.nodeData as NodeDataShape) ?? {};

        next.npcIds = parseNpcIds(npcIdsText);
        next.radius = radius;
        next.minPeriodSeconds = minPeriodSeconds;
        next.maxPeriodSeconds = maxPeriodSeconds;
        next.minDistFromCenter = minDistFromCenter;

        node.data = {
          ...nodeDataCloned,
          nodeData: next,
        };

        return nodes;
      });
    },
    [reactFlow, nodeId]
  );

  return (
    <FlowNodeWithChildren {...props}>
      <form className={"nodrag"} onSubmit={handleSubmit(handleUpdate)} onBlur={handleSubmit(handleUpdate)}>
        <Stack spacing={3}>
          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                NPC IDs (comma/space separated)
              </Text>
            </FormLabel>
            <Input {...register("npcIdsText")} color={color} placeholder={"npc_foo,npc_bar"} />
          </FormControl>

          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Radius (px)
              </Text>
            </FormLabel>
            <NumberInput isRequired step={1} defaultValue={radius} min={0}>
              <NumberInputField id={"radius"} {...register("radius", { valueAsNumber: true })} color={color} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Min period (seconds)
              </Text>
            </FormLabel>
            <NumberInput isRequired step={0.5} defaultValue={minPeriodSeconds} min={0}>
              <NumberInputField
                id={"minPeriodSeconds"}
                {...register("minPeriodSeconds", { valueAsNumber: true })}
                color={color}
              />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Max period (seconds)
              </Text>
            </FormLabel>
            <NumberInput isRequired step={0.5} defaultValue={maxPeriodSeconds} min={0}>
              <NumberInputField
                id={"maxPeriodSeconds"}
                {...register("maxPeriodSeconds", { valueAsNumber: true })}
                color={color}
              />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>

          <FormControl>
            <FormLabel>
              <Text casing={"uppercase"} color={color}>
                Min dist from center (px)
              </Text>
            </FormLabel>
            <NumberInput isRequired step={1} defaultValue={minDistFromCenter} min={0}>
              <NumberInputField
                id={"minDistFromCenter"}
                {...register("minDistFromCenter", { valueAsNumber: true })}
                color={color}
              />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </Stack>
      </form>
    </FlowNodeWithChildren>
  );
};

export default memo(NpcWanderingModeStartNode);
